import AddRounded from '@mui/icons-material/AddRounded';
import RequestQuoteRounded from '@mui/icons-material/RequestQuoteRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ContactPicker } from '../../components/pickers';
import { EmptyState } from '../../components/states';
import { money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useCopy } from '../../lib/useCopy';
import { todayIn } from '../inventory/model';
import { DetailHeader } from '../inventory/shared';
import {
  documentDiscountOf,
  emptyQuoteLine,
  fromQuotation,
  quoteFormSchema,
  toCreateRequest,
  toTotalsLines,
  toUpdateRequest,
  type QuoteFormValues,
} from './forms';
import { addDays, idem, quotationKeys, useTaxRates, type Quotation } from './model';
import { LINE_GRID } from './lineGrid';
import { DiscountTypeSwitch, QuoteLineRow } from './QuoteLineRow';
import { computeQuoteTotals } from './totals';

const HEADER_FIELDS = ['contact', 'docDate', 'validUntil', 'reference', 'notes', 'terms'] as const;

export function NewQuotationPage() {
  const { settings } = useShop();
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  // Duplicate: the copied quotation's customer, lines, notes and terms, dated today.
  const copy = useCopy() as Quotation | null;
  const contactId = copy?.contact.id ?? '';
  const contact = useQuery({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: contactId } } })),
    enabled: Boolean(contactId),
  });
  const initial = useMemo<QuoteFormValues>(
    () =>
      copy
        ? {
            ...fromQuotation(copy, contact.data ?? null),
            docDate: today,
            validUntil: addDays(today, 15),
            reference: '',
          }
        : {
            contact: null,
            docDate: today,
            validUntil: addDays(today, 15),
            reference: '',
            notes: '',
            terms: '',
            discountType: 'PERCENT',
            discountValue: '',
            lines: [emptyQuoteLine()],
          },
    [today, copy, contact.data],
  );
  return (
    <>
      <DetailHeader
        back="/quotations"
        title={copy ? `New quotation · copy of ${copy.number}` : 'New quotation'}
        subtitle="Price up items for a customer. Nothing is reserved or posted until it becomes an order."
      />
      {copy && contact.isLoading ? (
        <Stack spacing={2.5}>
          <Skeleton variant="rounded" height={150} />
          <Skeleton variant="rounded" height={280} />
        </Stack>
      ) : (
        <QuotationEditor key={copy?.id ?? 'new'} initial={initial} today={today} />
      )}
    </>
  );
}

export function EditQuotationPage() {
  const { id = '' } = useParams();
  const { settings } = useShop();
  const navigate = useNavigate();
  const today = todayIn(settings?.timeZone ?? 'Asia/Karachi');
  const quotation = useQuery({
    queryKey: quotationKeys.detail(id),
    queryFn: () => unwrap(api.GET('/quotations/{id}', { params: { path: { id } } })),
  });
  const contactId = quotation.data?.contact.id ?? '';
  const contact = useQuery({
    queryKey: ['contacts', 'detail', contactId],
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: contactId } } })),
    enabled: Boolean(contactId),
  });
  const q = quotation.data;
  const editable = q && (q.status === 'DRAFT' || q.status === 'SENT' || q.status === 'EXPIRED');

  if (quotation.error)
    return (
      <EmptyState
        icon={<RequestQuoteRounded />}
        title="Quotation not found"
        body="It may have been deleted or the link is wrong."
        action={{ label: 'Back to quotations', onClick: () => void navigate('/quotations') }}
      />
    );

  return (
    <>
      <DetailHeader back={`/quotations/${id}`} title={q ? `Edit ${q.number}` : 'Edit quotation'} />
      {!q || contact.isLoading ? (
        <Stack spacing={2.5}>
          <Skeleton variant="rounded" height={150} />
          <Skeleton variant="rounded" height={280} />
        </Stack>
      ) : !editable ? (
        <Card>
          <EmptyState
            icon={<RequestQuoteRounded />}
            title="This quotation is locked"
            body="Accepted, rejected and converted quotations can't be edited. Move it back to draft first."
            action={{ label: 'Open quotation', onClick: () => void navigate(`/quotations/${id}`) }}
          />
        </Card>
      ) : (
        <QuotationEditor
          key={q.version}
          initial={fromQuotation(q, contact.data ?? null)}
          quotation={q}
          today={today}
        />
      )}
    </>
  );
}

function QuotationEditor({
  initial,
  quotation,
  today,
}: {
  initial: QuoteFormValues;
  quotation?: Quotation;
  today: string;
}) {
  const { can } = useAuth();
  const { settings, format } = useShop();
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  // One client id per editor visit: a retried "create" cannot make two quotations.
  const clientId = useRef(crypto.randomUUID());
  const [focusLine, setFocusLine] = useState<number | null>(null);

  const schema = useMemo(() => quoteFormSchema(today), [today]);
  const form = useForm<QuoteFormValues>({ resolver: zodResolver(schema), defaultValues: initial });
  const { control, handleSubmit, setValue, setError, formState } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'lines', keyName: 'fieldId' });
  const values = useWatch({ control }) as QuoteFormValues;

  const hasTaxLines = values.lines.some((l) => l.taxRateId);
  const taxEnabled = Boolean(settings?.taxEnabled) || hasTaxLines;
  const taxRates = useTaxRates(taxEnabled);
  const snapshotRates = useMemo(
    () => new Map(quotation?.lines.map((l) => [l.taxRateId ?? '', l.taxRate]) ?? []),
    [quotation],
  );
  const rateOf = (id: string) =>
    taxRates.data?.find((t) => t.id === id)?.rate ?? snapshotRates.get(id);
  const pricesIncludeTax = quotation?.pricesIncludeTax ?? settings?.pricesIncludeTax ?? false;
  const decimals = settings?.currencyDecimals ?? format.currencyDecimals;
  const totals = computeQuoteTotals(toTotalsLines(values.lines, rateOf), {
    decimals,
    taxEnabled,
    pricesIncludeTax,
    documentDiscount: documentDiscountOf(values),
  });
  const currency = money(0, { ...format, currencyDecimals: 0 }).replace(/[\d\s.,]/g, '') || '¤';

  // Warn before closing the tab with unsaved changes.
  useEffect(() => {
    if (!formState.isDirty || formState.isSubmitSuccessful) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [formState.isDirty, formState.isSubmitSuccessful]);

  const save = (markSent: boolean) =>
    handleSubmit(async (v) => {
      if (totals.hasErrors) {
        notify.info('Check the highlighted quantities, prices and discounts.');
        return;
      }
      try {
        let saved: Quotation;
        if (quotation) {
          saved = await unwrap(
            api.PATCH('/quotations/{id}', {
              params: { path: { id: quotation.id } },
              body: toUpdateRequest(v, quotation.version),
            }),
          );
        } else {
          saved = await unwrap(
            api.POST('/quotations', {
              params: { header: idem(clientId.current) },
              body: toCreateRequest(v, clientId.current),
            }),
          );
        }
        if (markSent && saved.status === 'DRAFT') {
          saved = await unwrap(
            api.POST('/quotations/{id}/status', {
              params: { path: { id: saved.id } },
              body: { status: 'SENT', version: saved.version },
            }),
          );
        }
        queryClient.setQueryData(quotationKeys.detail(saved.id), saved);
        await queryClient.invalidateQueries({ queryKey: quotationKeys.all });
        notify.success(
          quotation
            ? `${saved.number} saved`
            : `${saved.number} created${markSent ? ' and marked sent' : ''}`,
        );
        void navigate(`/quotations/${saved.id}`, { replace: true });
      } catch (e) {
        if (e instanceof ApiError) {
          let mapped = false;
          for (const d of e.details) {
            const path = d.path.replace(/^body\./, '');
            const field = HEADER_FIELDS.find(
              (f) => path === f || (f === 'contact' && path === 'contactId'),
            );
            if (field) {
              setError(field, { message: d.message });
              mapped = true;
            }
          }
          if (!mapped || e.details.length > 1) notify.error(e);
        } else notify.error(e);
      }
    });

  const { errors } = formState;
  const canSend = can('quotations.update') && (!quotation || quotation.status === 'DRAFT');

  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void save(false)(e)}
      onKeyDown={(e) => {
        // ⌘S / Ctrl+S saves while editing.
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          void save(false)();
        }
      }}
    >
      <Card sx={{ mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: '1fr 1fr', lg: '2fr 1fr 1fr 1fr' },
            }}
          >
            <Box sx={{ gridColumn: { sm: '1 / -1', lg: 'auto' } }}>
              <Controller
                control={control}
                name="contact"
                render={({ field }) => (
                  <ContactPicker
                    type="customer"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    error={Boolean(errors.contact)}
                    helperText={errors.contact?.message}
                  />
                )}
              />
            </Box>
            <Controller
              control={control}
              name="docDate"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="Date"
                  error={Boolean(errors.docDate)}
                  helperText={errors.docDate?.message}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
                />
              )}
            />
            <Controller
              control={control}
              name="validUntil"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label="Valid until"
                  error={Boolean(errors.validUntil)}
                  helperText={errors.validUntil?.message ?? (field.value ? undefined : 'No expiry')}
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: values.docDate } }}
                />
              )}
            />
            <Controller
              control={control}
              name="reference"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reference"
                  placeholder="e.g. RFQ-118"
                  error={Boolean(errors.reference)}
                  helperText={errors.reference?.message}
                />
              )}
            />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Valid for
            </Typography>
            {[7, 15, 30].map((d) => (
              <Chip
                key={d}
                size="small"
                label={`${String(d)} days`}
                variant={values.validUntil === addDays(values.docDate, d) ? 'filled' : 'outlined'}
                color={values.validUntil === addDays(values.docDate, d) ? 'primary' : 'default'}
                onClick={() =>
                  setValue('validUntil', addDays(values.docDate, d), { shouldDirty: true })
                }
              />
            ))}
            <Chip
              size="small"
              label="No expiry"
              variant={values.validUntil === '' ? 'filled' : 'outlined'}
              color={values.validUntil === '' ? 'primary' : 'default'}
              onClick={() => setValue('validUntil', '', { shouldDirty: true })}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}
          >
            <Typography variant="h5">Items</Typography>
            <Typography variant="body2" color="text.secondary">
              {pricesIncludeTax ? 'Prices include tax' : 'Prices exclude tax'}
            </Typography>
          </Stack>
          <Box
            sx={{
              ...LINE_GRID,
              gridTemplateAreas: undefined,
              display: { xs: 'none', md: 'grid' },
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
              color: 'text.secondary',
              typography: 'caption',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            <span>#</span>
            <span>Item</span>
            <span style={{ textAlign: 'right' }}>Qty</span>
            <span>Unit</span>
            <span style={{ textAlign: 'right' }}>Price</span>
            <span style={{ textAlign: 'right' }}>Amount</span>
            <span />
          </Box>
          <Stack divider={<Divider flexItem />}>
            {fields.map((f, i) => (
              <QuoteLineRow
                key={f.fieldId}
                control={control}
                setValue={setValue}
                index={i}
                result={totals.lines[i]}
                showErrors={formState.isSubmitted}
                taxEnabled={taxEnabled}
                taxRates={taxRates.data ?? []}
                canDiscount={can('sales.discount')}
                canRemove={fields.length > 1}
                onRemove={() => remove(i)}
                autoFocus={focusLine === i}
              />
            ))}
          </Stack>
          {errors.lines?.message || errors.lines?.root?.message ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {errors.lines.message ?? errors.lines.root?.message}
            </Typography>
          ) : null}
          <Button
            startIcon={<AddRounded />}
            onClick={() => {
              setFocusLine(fields.length);
              append(emptyQuoteLine());
            }}
            sx={{ mt: 1 }}
          >
            Add item
          </Button>
        </CardContent>
      </Card>

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0, 1fr) 380px' },
          alignItems: 'start',
        }}
      >
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Notes & terms
            </Typography>
            <Stack spacing={2}>
              <Controller
                control={control}
                name="notes"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes for the customer"
                    multiline
                    minRows={3}
                    error={Boolean(errors.notes)}
                    helperText={errors.notes?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="terms"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Terms & conditions"
                    multiline
                    minRows={3}
                    placeholder="e.g. 50% advance, delivery within 7 days of confirmation."
                    error={Boolean(errors.terms)}
                    helperText={errors.terms?.message}
                  />
                )}
              />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={(t) => {
            const p = (t.vars ?? t).palette.primary.main;
            return {
              position: { md: 'sticky' },
              top: { md: 88 },
              background: `linear-gradient(160deg, color-mix(in srgb, ${p} 7%, transparent), transparent 60%)`,
            };
          }}
        >
          <CardContent>
            <Stack spacing={1.25}>
              <TotalRow label="Subtotal" value={money(totals.subtotal, format)} />
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Controller
                  control={control}
                  name="discountValue"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Discount on total"
                      placeholder="0"
                      error={Boolean(totals.documentDiscountError)}
                      helperText={totals.documentDiscountError}
                      sx={{ width: 170 }}
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: {
                          inputMode: 'decimal',
                          className: 'tabular',
                          style: { textAlign: 'right' },
                        },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Controller
                                control={control}
                                name="discountType"
                                render={({ field: t }) => (
                                  <DiscountTypeSwitch
                                    value={t.value}
                                    onChange={t.onChange}
                                    currency={currency}
                                  />
                                )}
                              />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                />
                <Typography className="tabular" sx={{ color: 'success.main', fontWeight: 600 }}>
                  {Number(totals.documentDiscount)
                    ? `−${money(totals.documentDiscount, format)}`
                    : '—'}
                </Typography>
              </Stack>
              {Number(totals.discountTotal) > 0 && (
                <TotalRow
                  label="All discounts"
                  value={`−${money(totals.discountTotal, format)}`}
                  muted
                />
              )}
              {(taxEnabled || Number(totals.taxTotal) > 0) && (
                <>
                  <TotalRow
                    label={pricesIncludeTax ? 'Net of tax' : 'Net'}
                    value={money(totals.netTotal, format)}
                    muted
                  />
                  <TotalRow
                    label={pricesIncludeTax ? 'Tax included' : 'Tax'}
                    value={money(totals.taxTotal, format)}
                    muted
                  />
                </>
              )}
              <Divider />
              <Stack
                direction="row"
                sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h4" className="tabular" sx={{ fontWeight: 750 }}>
                  {money(totals.total, format)}
                </Typography>
              </Stack>
            </Stack>
            <Stack spacing={1.25} sx={{ mt: 2.5 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                loading={formState.isSubmitting}
              >
                {quotation ? 'Save changes' : 'Save quotation'}
              </Button>
              {canSend && (
                <Button
                  variant="outlined"
                  startIcon={<SendRounded />}
                  disabled={formState.isSubmitting}
                  onClick={() => void save(true)()}
                >
                  Save and mark sent
                </Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                Totals are checked again by the server when you save · Ctrl/⌘ S
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function TotalRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        className="tabular"
        variant="body2"
        sx={{ fontWeight: muted ? 500 : 650, color: muted ? 'text.secondary' : 'text.primary' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
